/**
 * Shared chart contracts.
 *
 * Chat previews and chart tools pass model-produced visualization payloads
 * through these schemas so they are validated before the browser renders them.
 *
 * @module types/chart
 */

import { z } from "zod";

export const chartTypeSchema = z.enum(["bar", "line", "pie", "area"]);

export const chartDataRowSchema = z
  .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .refine((row) => Object.keys(row).length > 0, "Chart rows cannot be empty");

export const chartConfigEntrySchema = z.object({
  label: z.string().optional(),
  color: z.string().optional(),
});

export const chartSpecSchema = z.object({
  id: z.string().optional(),
  type: chartTypeSchema,
  title: z.string().min(1),
  description: z.string().optional(),
  data: z.array(chartDataRowSchema).min(1),
  chartConfig: z.record(z.string(), chartConfigEntrySchema).default({}),
  xKey: z.string().optional(),
  yKey: z.string().optional(),
  seriesKey: z.string().optional(),
  tooltipFields: z.array(z.string()).optional(),
});

export type ChartType = z.infer<typeof chartTypeSchema>;
export type ChartDataRow = z.infer<typeof chartDataRowSchema>;
export type ChartConfigEntry = z.infer<typeof chartConfigEntrySchema>;
export type ChartSpec = z.infer<typeof chartSpecSchema>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function slugKey(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "series"
  );
}

function chartConfigFromRows(rows: ChartDataRow[]): Record<string, ChartConfigEntry> {
  return rows.reduce<Record<string, ChartConfigEntry>>((acc, row, index) => {
    const name = typeof row.name === "string" ? row.name : `Series ${index + 1}`;
    const color = typeof row.color === "string" ? row.color : undefined;
    acc[slugKey(name)] = {
      label: name,
      color,
    };
    return acc;
  }, {});
}

/**
 * Converts known legacy and streamed chart payloads into the shared chart spec.
 */
export function normalizeChartSpec(value: unknown): ChartSpec | null {
  if (!isRecord(value)) return null;

  if (value.type === "data-chart" && isRecord(value.data)) {
    return normalizeChartSpec(value.data);
  }

  if (isRecord(value.chartData)) {
    return normalizeChartSpec(value.chartData);
  }

  if (isRecord(value.chart)) {
    const chart = value.chart;
    const normalized = {
      type: chart.type,
      title: typeof chart.title === "string" ? chart.title : chart.name,
      data: chart.data,
      chartConfig: isRecord(chart.chartConfig) ? chart.chartConfig : undefined,
    };
    const parsed = chartSpecSchema.safeParse(normalized);
    if (!parsed.success) return null;
    return {
      ...parsed.data,
      chartConfig:
        Object.keys(parsed.data.chartConfig).length > 0
          ? parsed.data.chartConfig
          : chartConfigFromRows(parsed.data.data),
    };
  }

  const parsed = chartSpecSchema.safeParse(value);
  if (!parsed.success) return null;
  return {
    ...parsed.data,
    chartConfig:
      Object.keys(parsed.data.chartConfig).length > 0
        ? parsed.data.chartConfig
        : chartConfigFromRows(parsed.data.data),
  };
}
